import type { PublicTransfer } from '../types'
import { formatFileSize } from '../crypto'
import { FileIcon } from './FileIcon'

interface TransferInfoProps {
  transfer: PublicTransfer
  onDownload: () => void
}

export function TransferInfo({ transfer, onDownload }: TransferInfoProps) {
  const file = transfer.files[0]
  
  // Format expiration
  const formatExpiry = (expiresAt: string | null) => {
    if (!expiresAt) return 'Never expires'
    
    const expiry = new Date(expiresAt)
    const now = new Date()
    const diff = expiry.getTime() - now.getTime()
    
    if (diff < 0) return 'Expired'
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24))
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
    
    if (days > 0) {
      return `Expires in ${days} day${days === 1 ? '' : 's'}`
    }
    if (hours > 0) {
      return `Expires in ${hours} hour${hours === 1 ? '' : 's'}`
    }
    return 'Expires soon'
  }

  return (
    <div>
      {/* Security badge */}
      <div style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--space-xs)',
        padding: 'var(--space-xs) var(--space-sm)',
        background: 'rgba(0, 212, 170, 0.1)',
        borderRadius: 'var(--radius-full)',
        marginBottom: 'var(--space-lg)',
        fontSize: '12px',
        color: 'var(--accent)',
        fontWeight: 500,
      }}>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
          <path d="M12 22C12 22 20 18 20 12V5L12 2L4 5V12C4 18 12 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
          <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        End-to-end encrypted
      </div>

      {/* File info */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 'var(--space-md)',
        marginBottom: 'var(--space-lg)',
        padding: 'var(--space-md)',
        background: 'var(--bg)',
        borderRadius: 'var(--radius-md)',
      }}>
        <FileIcon filename={file?.name || ''} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ 
            fontWeight: 500,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}>
            {file?.name || 'Encrypted file'}
          </div>
          <div style={{ 
            fontSize: '13px', 
            color: 'var(--text-muted)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-sm)',
          }}>
            <span>{formatFileSize(transfer.total_size)}</span>
            {transfer.files.length > 1 && (
              <>
                <span style={{ color: 'var(--border-light)' }}>&middot;</span>
                <span>{transfer.files.length} files</span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Meta info */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 'var(--space-lg)',
        marginBottom: 'var(--space-lg)',
        fontSize: '13px',
        color: 'var(--text-muted)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)' }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
            <path d="M12 6V12L16 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          {formatExpiry(transfer.expires_at)}
        </div>
        
        {transfer.download_limit && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path d="M21 15V19C21 20.1046 20.1046 21 19 21H5C3.89543 21 3 20.1046 3 19V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              <path d="M12 3V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
            {transfer.download_count} / {transfer.download_limit} downloads
          </div>
        )}
      </div>

      {/* Download button */}
      <button 
        className="btn btn-primary btn-large"
        onClick={onDownload}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
          <path d="M21 15V19C21 20.1046 20.1046 21 19 21H5C3.89543 21 3 20.1046 3 19V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          <path d="M12 3V15M12 15L7 10M12 15L17 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        Download File
      </button>

      {/* Security note */}
      <p style={{ 
        marginTop: 'var(--space-lg)',
        fontSize: '12px',
        color: 'var(--text-dim)',
        textAlign: 'center',
        lineHeight: 1.5,
      }}>
        This file is encrypted. Decryption happens entirely in your browser using the key embedded in the link. We never have access to your files.
      </p>
    </div>
  )
}
